// Shared TanStack QueryClient. The retry policy intentionally only retries
// server-side failures (5xx) — client-side 4xx errors are deterministic and
// retrying them just wastes a network round-trip + delays the error UI.
//
// Duration constants are inlined as private consts: per architecture-principles
// §11, core/ must not import from shared/. Shared exports the same MS
// constants for feature consumers; keep them in sync if the cache windows
// change.

import { QueryClient } from '@tanstack/vue-query'
import type { AppError } from '@core/api/normalizeError'

// Every query / mutation error is an AppError: the axios interceptors run
// thrown values through normalizeError, and parsers throw AppError-shaped
// objects. Registering it types `error` refs app-wide, so callers read
// `error.value?.message` without casting.
declare module '@tanstack/vue-query' {
  interface Register {
    defaultError: AppError
  }
}

const ONE_MINUTE_MS = 60_000
const FIVE_MINUTES_MS = 5 * 60_000
const MAX_RETRIES = 2
const SERVER_ERROR_STATUS_MIN = 500

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: ONE_MINUTE_MS,
      gcTime: FIVE_MINUTES_MS,
      refetchOnWindowFocus: false,
      retry: (count, err) =>
        count < MAX_RETRIES && (err?.statusCode ?? 0) >= SERVER_ERROR_STATUS_MIN,
    },
  },
})
